import React, { useState, useEffect } from 'react';
import { CallLog } from '@/interfaces/ILogs';
import { TranscriptMessage } from '@/types/TCallLogs';
import { useMutation } from '@tanstack/react-query';
import { getCallRecording } from '@/api/logs';
import { Collapse, message, Typography } from 'antd';
import { FileTextOutlined, MessageOutlined } from '@ant-design/icons';
import { CallInformationCard } from '../../../CallInformation/CallInformationCard';
import { CallTranscriptCard } from '../../../CallInformation/CallTranscriptCard';
import { CallSummaryCard } from '../../../CallInformation/CallSummaryCard';

const { Text } = Typography;

interface CallDetailsViewProps {
	callLog: CallLog;
}

export const CallDetailsView: React.FC<CallDetailsViewProps> = ({ callLog }) => {
	const [audioURL, setAudioURL] = useState<string | null>(null);
	const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

	const { mutate: callRecordingMutation, isPending: isAudioLoading } = useMutation({
		mutationKey: ['getCallRecordingView', callLog.callSid],
		mutationFn: getCallRecording,
		onSuccess: (data) => setAudioURL(data.url),
		onError: () => message.error('Failed to load call recording.'),
	});

	useEffect(() => {
		if (callLog?.isRecorded === 'true' && callLog.callSid) {
			callRecordingMutation(callLog.callSid);
		}
	}, [callLog, callRecordingMutation]);

	const transcriptMessages: TranscriptMessage[] = callLog?.transcript
		? (callLog.transcript
				.split('\n')
				.map((message: string, index: number) => {
					if (message.startsWith('assistant@')) {
						return { key: index, sender: 'Assistant', message: message.replace('assistant@', '') };
					} else if (message.startsWith('user@')) {
						return { key: index, sender: 'User', message: message.replace('user@', '') };
					}
					return null;
				})
				.filter(Boolean) as TranscriptMessage[])
		: [];

	const collapseItems = [
		callLog.callSummary && {
			key: 'summary',
			label: (
				<div className="flex items-center py-2">
					<FileTextOutlined className="text-blue-500 mr-3 text-xl" />
					<Text strong className="text-base">
						Call Summary
					</Text>
				</div>
			),
			children: <CallSummaryCard callSummary={callLog.callSummary} />,
		},
		transcriptMessages.length > 0 && {
			key: 'transcript',
			label: (
				<div className="flex items-center py-2">
					<MessageOutlined className="text-green-500 mr-3 text-xl" />
					<Text strong className="text-base">
						Call Transcript
					</Text>
				</div>
			),
			children: <CallTranscriptCard transcriptMessages={transcriptMessages} />,
		},
	].filter(Boolean);

	return (
		<div style={{ maxHeight: '75vh', overflowY: 'auto', padding: '1px' }}>
			<div className="space-y-6">
				<CallInformationCard
					callInformation={callLog}
					audioURl={audioURL}
					audioLoading={isAudioLoading}
					playbackSpeed={playbackSpeed}
					onPlaybackSpeedChange={setPlaybackSpeed}
				/>

				{collapseItems.length > 0 && (
					<Collapse
						defaultActiveKey={['summary', 'transcript']}
						bordered={false}
						ghost
						items={collapseItems}
						className="bg-white rounded-xl shadow-lg border-0"
					/>
				)}
			</div>
		</div>
	);
};
